"use client";

import { useCallback } from "react";
import { useSessionOwnCollabReadAdvancement } from "./use-session-own-collab-advance";
import { useSessionProductReadMarkers } from "./use-session-product-read-markers";
import { useSessionProductsSeenState } from "./use-session-products-seen-state";
import { useSessionReadMarkerActions } from "./use-session-read-marker-actions";
import { useSessionReadOwnMessageIds } from "./use-session-read-own-message-ids";
import type {
  ResetReadTrackingSegment,
  SessionProductReadMarkers,
  SessionProductsSeenState,
  SessionReadMarkerActions,
  SessionReadOwnMessageIds,
  SessionReadTracking,
  SessionReadUnreadState,
  TagOwnCollabsAndAdvanceRead,
  UseSessionReadTrackingInput,
} from "./use-session-read-tracking.types";
import { useSessionReadUnreadState } from "./use-session-read-unread-state";

export type { SessionReadTracking } from "./use-session-read-tracking.types";

interface UseSessionReadTrackingResetInput {
  resetOwnMessageIds: ResetReadTrackingSegment;
  resetProductReadMarkers: ResetReadTrackingSegment;
  resetProductsSeen: ResetReadTrackingSegment;
}

interface SelectSessionReadTrackingInput {
  ownMessageState: SessionReadOwnMessageIds;
  productReadState: SessionProductReadMarkers;
  productsSeenState: SessionProductsSeenState;
  resetReadTracking: () => void;
  tagOwnCollabsAndAdvanceRead: TagOwnCollabsAndAdvanceRead;
  readMarkerActions: SessionReadMarkerActions;
  unreadState: SessionReadUnreadState;
}

export function useSessionReadTracking(
  input: UseSessionReadTrackingInput,
): SessionReadTracking {
  const {
    currentUserId,
    products,
    chatMessages,
    chatMessageIds,
    collabs,
    collabsByProduct,
  } = input;

  const ownMessageState = useSessionReadOwnMessageIds();
  const productReadState = useSessionProductReadMarkers();
  const productsSeenState = useSessionProductsSeenState();
  const resetReadTracking = useSessionReadTrackingReset({
    resetOwnMessageIds: ownMessageState.resetOwnMessageIds,
    resetProductReadMarkers: productReadState.resetProductReadMarkers,
    resetProductsSeen: productsSeenState.resetProductsSeen,
  });

  const tagOwnCollabsAndAdvanceRead = useSessionOwnCollabReadAdvancement({
    currentUserId,
    addOwnMessageIds: ownMessageState.addOwnMessageIds,
    updateLocalProductReadMarker: productReadState.updateLocalProductReadMarker,
  });
  const readMarkerActions = useSessionReadMarkerActions({
    chatMessageIds,
    chatMessages,
    collabs,
    collabsByProduct,
    updateLocalProductReadMarker: productReadState.updateLocalProductReadMarker,
  });
  const unreadState = useSessionReadUnreadState({
    products,
    chatMessageIds,
    chatMessages,
    collabs,
    collabsByProduct,
    ownMessageIds: ownMessageState.ownMessageIds,
    productReads: productReadState.productReads,
  });

  return selectSessionReadTracking({
    ownMessageState,
    productReadState,
    productsSeenState,
    resetReadTracking,
    tagOwnCollabsAndAdvanceRead,
    readMarkerActions,
    unreadState,
  });
}

function useSessionReadTrackingReset(
  input: UseSessionReadTrackingResetInput,
): () => void {
  const { resetOwnMessageIds, resetProductReadMarkers, resetProductsSeen } =
    input;

  return useCallback((): void => {
    resetProductReadMarkers();
    resetProductsSeen();
    resetOwnMessageIds();
  }, [resetOwnMessageIds, resetProductReadMarkers, resetProductsSeen]);
}

function selectSessionReadTracking(
  input: SelectSessionReadTrackingInput,
): SessionReadTracking {
  const {
    ownMessageState,
    productReadState,
    productsSeenState,
    resetReadTracking,
    tagOwnCollabsAndAdvanceRead,
    readMarkerActions,
    unreadState,
  } = input;

  return {
    productReads: productReadState.productReads,
    productsSeen: productsSeenState.productsSeen,
    unreadCollabCounts: unreadState.unreadCollabCounts,
    unread: unreadState.unread,
    ownMessageIdsRef: ownMessageState.ownMessageIdsRef,
    addOwnMessageIds: ownMessageState.addOwnMessageIds,
    resetReadTracking,
    replaceProductsSeen: productsSeenState.replaceProductsSeen,
    updateProductsSeen: productsSeenState.updateProductsSeen,
    mergeProductReadItems: productReadState.mergeProductReadItems,
    updateLocalProductReadMarker: productReadState.updateLocalProductReadMarker,
    tagOwnCollabsAndAdvanceRead,
    isCollabUnread: unreadState.isCollabUnread,
    ...readMarkerActions,
  };
}
